import { useEffect, useRef, useState } from 'react';

const GALLERY_IMAGES = [
  {
    src: 'https://static.readdy.ai/image/17c770440fc9a0cd846fe980a29eae0b/4f35ccf3cf6bf783d4223f7b379a1c67.jpeg',
    label: 'Night Campaign',
    location: 'Downtown Tampa',
  },
  {
    src: 'https://static.readdy.ai/image/17c770440fc9a0cd846fe980a29eae0b/693f00f1fa15e6b0c71a144cc95ed68b.jpeg',
    label: 'Store Ad',
    location: 'Amalie Arena',
  },
  {
    src: 'https://static.readdy.ai/image/17c770440fc9a0cd846fe980a29eae0b/f65bf952f522fe2633bc34b3cf1c59c4.jpeg',
    label: 'Side Display',
    location: 'Ybor City',
  },
  {
    src: 'https://static.readdy.ai/image/17c770440fc9a0cd846fe980a29eae0b/20f063b939068b7ead7cb074a172be6e.jpeg',
    label: 'Event Promotion',
    location: 'Westshore District',
  },
  {
    src: 'https://static.readdy.ai/image/17c770440fc9a0cd846fe980a29eae0b/f36e041b14272d215dba9f780bc1271b.jpeg',
    label: 'Highway Exposure',
    location: 'I-275 Tampa',
  },
  {
    src: 'https://static.readdy.ai/image/17c770440fc9a0cd846fe980a29eae0b/5c84c374aac12e9179d67747f235d09d.jpeg',
    label: 'New Launch',
    location: 'South Tampa',
  },
];

const SPOTLIGHT_IMAGES = [
  {
    src: 'https://static.readdy.ai/image/17c770440fc9a0cd846fe980a29eae0b/9eb4514667a418c11851cc3320c5f59a.jpeg',
    label: 'Real State',
    location: 'Tampa Heights',
    stat: '3,200 Impressions',
  },
  {
    src: 'https://static.readdy.ai/image/17c770440fc9a0cd846fe980a29eae0b/c6368dea72778cdd8cbefb9f3f04aec8.jpeg',
    label: 'Event Night',
    location: 'Armature Works',
    stat: '4,800 Impressions',
  },
];

const BONUS_IMAGES = [
  {
    src: 'https://static.readdy.ai/image/17c770440fc9a0cd846fe980a29eae0b/879386ab737e9430338e24bbf50b3c70.jpeg',
    label: 'Retail Zone',
    location: 'International Plaza',
  },
  {
    src: 'https://static.readdy.ai/image/17c770440fc9a0cd846fe980a29eae0b/7529e237b48bec0cdf39783d7aae0a03.jpeg',
    label: 'Game Day',
    location: 'Buccaneer Stadium',
  },
  {
    src: 'https://static.readdy.ai/image/17c770440fc9a0cd846fe980a29eae0b/05e6efcaf2f3178489b61fc7438164db.jpeg',
    label: 'Political Event',
    location: 'Raymond James Stadium',
  },
  {
    src: 'https://static.readdy.ai/image/17c770440fc9a0cd846fe980a29eae0b/708231fec62af6c0c8a1166d0cb09952.jpeg',
    label: 'private Party',
    location: 'Leto',
  },
];

interface GalleryCardProps {
  src: string;
  label: string;
  location: string;
  height?: string;
  delay?: number;
  visible: boolean;
}

const GalleryCard = ({ src, label, location, height = '280px', delay = 0, visible }: GalleryCardProps) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 group ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
      style={{ height, transitionDelay: `${delay}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img
        src={src}
        alt={label}
        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
      />
      <div
        className="absolute inset-0 transition-all duration-300"
        style={{
          background: hovered
            ? 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)'
            : 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)',
        }}
      />
      <div
        className="absolute inset-0 rounded-2xl transition-all duration-300 pointer-events-none"
        style={{
          border: `2px solid ${hovered ? '#00d4ff' : 'transparent'}`,
          boxShadow: hovered ? '0 0 20px rgba(0,212,255,0.3), inset 0 0 20px rgba(0,212,255,0.05)' : 'none',
        }}
      />
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div
          className="inline-block px-2.5 py-1 rounded-full text-xs font-rajdhani font-bold mb-2"
          style={{ backgroundColor: 'rgba(0,212,255,0.2)', color: '#00d4ff', border: '1px solid rgba(0,212,255,0.3)' }}
        >
          {label}
        </div>
        <div className="flex items-center justify-between">
          <p className="text-white text-sm font-rajdhani font-semibold">
            <i className="ri-map-pin-line mr-1" />
            {location}
          </p>
          <span
            className="text-xs font-rajdhani font-bold"
            style={{ color: '#39ff14', textShadow: '0 0 8px #39ff14' }}
          >
            <i className="ri-eye-line mr-1" />Live
          </span>
        </div>
      </div>
    </div>
  );
};

const GallerySection = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="gallery" className="relative py-24 md:py-32 bg-dark-charcoal overflow-hidden" ref={sectionRef}>
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, #b026ff, #ff073a, transparent)' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">

        {/* ── Header ── */}
        <div
          className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div
            className="inline-block text-xs font-rajdhani font-bold tracking-widest uppercase mb-4 px-3 py-1 rounded-full"
            style={{ color: '#00d4ff', border: '1px solid #00d4ff', boxShadow: '0 0 10px rgba(0,212,255,0.3)' }}
          >
            Visual Proof
          </div>
          <h2
            className="font-orbitron font-black text-4xl md:text-5xl lg:text-6xl"
            style={{ color: '#ffffff', textShadow: '0 0 30px rgba(0,212,255,0.3)' }}
          >
            SEE IT{' '}
            <span style={{ color: '#00d4ff', textShadow: '0 0 20px #00d4ff' }}>IN ACTION</span>
          </h2>
          <p className="mt-4 text-gray-400 text-lg font-rajdhani">
            Real campaigns. Real streets. Real exposure.
          </p>
        </div>

        {/* ── Section 1: Main Grid (6 images) ── */}
        <div
          className={`flex items-center gap-3 mb-6 transition-all duration-700 delay-100 ${visible ? 'opacity-100' : 'opacity-0'}`}
        >
          <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, #00d4ff40)' }} />
          <span className="text-xs font-rajdhani font-bold tracking-widest uppercase" style={{ color: '#00d4ff' }}>
            <i className="ri-camera-line mr-2" />Campaign Highlights
          </span>
          <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, #00d4ff40, transparent)' }} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {GALLERY_IMAGES.map((img, i) => (
            <GalleryCard
              key={i}
              src={img.src}
              label={img.label}
              location={img.location}
              height={i % 3 === 0 ? '320px' : i % 2 === 0 ? '260px' : '290px'}
              delay={i * 80}
              visible={visible}
            />
          ))}
        </div>

        {/* ── Section 2: Spotlight (2 wide images) ── */}
        <div
          className={`flex items-center gap-3 mb-6 transition-all duration-700 delay-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
        >
          <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, #39ff1440)' }} />
          <span className="text-xs font-rajdhani font-bold tracking-widest uppercase" style={{ color: '#39ff14' }}>
            <i className="ri-flashlight-fill mr-2" />Spotlight Moments
          </span>
          <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, #39ff1440, transparent)' }} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-16">
          {SPOTLIGHT_IMAGES.map((img, i) => (
            <div
              key={i}
              className={`relative rounded-2xl overflow-hidden group cursor-pointer transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ height: '340px', transitionDelay: `${600 + i * 120}ms` }}
            >
              <img
                src={img.src}
                alt={img.label}
                className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div
                className="absolute inset-0 rounded-2xl transition-all duration-300 pointer-events-none opacity-0 group-hover:opacity-100"
                style={{ border: '2px solid #39ff14', boxShadow: '0 0 24px rgba(57,255,20,0.3)' }}
              />
              {/* Top stat badge */}
              <div
                className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-xs font-rajdhani font-bold"
                style={{ backgroundColor: 'rgba(0,0,0,0.85)', color: '#39ff14', border: '1px solid #39ff14', boxShadow: '0 0 10px rgba(57,255,20,0.4)' }}
              >
                <i className="ri-bar-chart-fill mr-1" />{img.stat}
              </div>
              {/* Bottom info */}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div
                  className="inline-block px-2.5 py-1 rounded-full text-xs font-rajdhani font-bold mb-2"
                  style={{ backgroundColor: 'rgba(57,255,20,0.15)', color: '#39ff14', border: '1px solid rgba(57,255,20,0.4)' }}
                >
                  {img.label}
                </div>
                <p className="text-white text-sm font-rajdhani font-semibold">
                  <i className="ri-map-pin-line mr-1" />{img.location}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Section 3: 4-image City Coverage Grid ── */}
        <div
          className={`flex items-center gap-3 mb-6 transition-all duration-700 delay-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
        >
          <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, #b026ff40)' }} />
          <span className="text-xs font-rajdhani font-bold tracking-widest uppercase" style={{ color: '#b026ff' }}>
            <i className="ri-map-2-line mr-2" />City Coverage
          </span>
          <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, #b026ff40, transparent)' }} />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {BONUS_IMAGES.map((img, i) => (
            <GalleryCard
              key={i}
              src={img.src}
              label={img.label}
              location={img.location}
              height="220px"
              delay={800 + i * 80}
              visible={visible}
            />
          ))}
        </div>

        {/* ── Section 4: Full-width Truck Banner ── */}
        <div
          className={`rounded-3xl overflow-hidden transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{
            transitionDelay: '1100ms',
            border: '2px solid rgba(57,255,20,0.3)',
            boxShadow: '0 0 40px rgba(57,255,20,0.1)',
          }}
        >
          <div className="relative">
            <img
              src="https://static.readdy.ai/image/17c770440fc9a0cd846fe980a29eae0b/e1386af19a9f43d6d7f9ede855a5ef8f.jpeg"
              alt="MartLed truck — your ad here"
              className="w-full h-48 md:h-72 object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-black/70" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <p
                className="font-orbitron font-black text-xl md:text-3xl lg:text-4xl mb-3 whitespace-nowrap"
                style={{ color: '#39ff14', textShadow: '0 0 20px #39ff14, 0 0 60px rgba(57,255,20,0.4)' }}
              >
                
              </p>
              <p className="text-gray-300 font-rajdhani text-base md:text-lg tracking-wide">
                10,000+ eyes on your ad every week across Tampa Bay
              </p>
              <div className="flex gap-6 mt-5">
                {['Downtown', 'Ybor City', 'Westshore', 'South Tampa', 'St. Pete'].map((zone) => (
                  <span
                    key={zone}
                    className="hidden sm:block text-xs font-rajdhani font-bold tracking-widest uppercase"
                    style={{ color: '#00d4ff', textShadow: '0 0 8px #00d4ff' }}
                  >
                    {zone}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default GallerySection;
